/**
 * The student account has one public, immutable identifier. During the API
 * migration old endpoints may still expose it as `display_code`, so consumers
 * deliberately read `login` first and use that legacy alias only as fallback.
 */
export function studentLoginValue(value: {
  login?: string | null
  display_code?: string | null
}): string | null {
  return value.login || value.display_code || null
}

/** UI-only rendering helper; never use its placeholder as an API input. */
export function studentLogin(value: {
  login?: string | null
  display_code?: string | null
}): string {
  return studentLoginValue(value) ?? '—'
}

/**
 * Transitional write shape understood by both sides of the TR-1002 cutover.
 *
 * The pre-cutover reset endpoint requires `display_code`; the new endpoint
 * prefers `login` and accepts `display_code` only when it is identical. Roster
 * rows expose the effective identifier under `display_code` in both versions,
 * so dual-writing one value keeps a rollback and the final cutover compatible.
 */
export function studentLoginWireFields(login: string): {
  login: string
  display_code: string
} {
  return { login, display_code: login }
}

/** Canonical presentation and submission form for a student login. */
export function normalizeStudentLogin(value: string): string {
  return value.trim().toUpperCase()
}

/** `SCHOOL-CLASS-N`: the exact backend and mobile contract for a student login. */
export function isValidStudentLogin(value: string): boolean {
  return /^[A-Z0-9]{2,16}-[A-Z]{3}-[1-9][0-9]{0,4}$/.test(value)
}
